import React, { useEffect, useRef, useState } from "react";
import InputMask from "react-input-mask";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded";
import { Input, Select } from "antd";
import { getDownloadURL, ref, uploadBytesResumable } from "firebase/storage";
import { useSelector } from "react-redux";
import { storage } from "../../../config/firebase";
import { newClientThemeVars } from "../../../theme/globalStyle/globalStyle";
import theme from "../../../theme";
import vehicleService from "../../../service/vehicle.service";
import {
  focusFirstInvalidField,
  isReboqueStarted,
  isVehicleTypeRequiringReboque,
  validateVehicleForm,
} from "../validation";
import DocumentField from "./DocumentField";
import "../vehicle.css";

const STATES_UF = [
  { value: "AC", label: "Acre" },
  { value: "AL", label: "Alagoas" },
  { value: "AP", label: "Amapá" },
  { value: "AM", label: "Amazonas" },
  { value: "BA", label: "Bahia" },
  { value: "CE", label: "Ceará" },
  { value: "DF", label: "Distrito Federal" },
  { value: "ES", label: "Espírito Santo" },
  { value: "GO", label: "Goiás" },
  { value: "MA", label: "Maranhão" },
  { value: "MT", label: "Mato Grosso" },
  { value: "MS", label: "Mato Grosso do Sul" },
  { value: "MG", label: "Minas Gerais" },
  { value: "PA", label: "Pará" },
  { value: "PB", label: "Paraíba" },
  { value: "PR", label: "Paraná" },
  { value: "PE", label: "Pernambuco" },
  { value: "PI", label: "Piauí" },
  { value: "RJ", label: "Rio de Janeiro" },
  { value: "RN", label: "Rio Grande do Norte" },
  { value: "RS", label: "Rio Grande do Sul" },
  { value: "RO", label: "Rondônia" },
  { value: "RR", label: "Roraima" },
  { value: "SC", label: "Santa Catarina" },
  { value: "SP", label: "São Paulo" },
  { value: "SE", label: "Sergipe" },
  { value: "TO", label: "Tocantins" },
];

const VEHICLE_TYPES = [
  "FIORINO",
  "TRUCK",
  "BI-TRUCK",
  "CARRETA S",
  "CARRETA LS",
  "TOCO",
  "3/4",
  "VUC",
  "BITREM",
  "RODOTREM",
  "MUNK",
  "VANDERLEIA",
];

const BODYWORK_TYPES = [
  "BOBINEIRA",
  "GRADE BAIXA",
  "BAÚSECO",
  "BAÚ FRIGORIFICO",
  "ABERTO",
  "BAÚ",
  "DOLLY",
  "GRANELEIRO",
  "SIDER",
  "PRANCHA",
  "TANQUE",
  "CAÇAMBA",
  "PORTA CONTEINER",
  "CILO",
  "CEGONHA",
];

const RNTRC_TYPE_OPTIONS = [
  { value: "tac", label: "TAC" },
  { value: "etc", label: "ETC" },
  { value: "ctc", label: "CTC" },
];

const ALERT_TITLE_ID = "quick-vehicle-alert-dialog-title";
const ALERT_DESCRIPTION_ID = "quick-vehicle-alert-dialog-description";

const emptyReboque = () => ({
  id: "",
  placaReboque: "",
  renavamReboque: "",
  ufReboque: "",
  descricaoReboque: "",
  rntrcReboque: "",
  tipoRNTRCReboque: "",
  TipoCarroceriaReboque: "",
  anttReboque: "",
  cnhReboque: "",
  capacidadeKGReboque: "",
  capacidadeM3Reboque: "",
  pesoTaraReboque: "",
  tipoProprietarioReboque: "cpf",
  cpfoucnpjReboque: "",
  nomeProprietarioReboque: "",
});

const createInitialFormState = () => ({
  vehiclePlate: "",
  renavam: "",
  ufVeiculo: "",
  documentCRLV: "",
  descricaoVeiculo: "",
  vehicleType: "",
  rntrc: "",
  tipoRNTRC: "",
  bodyworkType: "",
  bodyworkPlate: "",
  documentAnttFrontImg: "",
  pesoTara: "",
  capacidadeKG: "",
  capacidadeM3: "",
  tipoProprietario: "",
  cpfoucnpj: "",
  nomeProprietario: "",
  vehicleImg: "",
  reboques: [],
});

function parseDialogMessageItems(msg) {
  if (Array.isArray(msg)) {
    return msg.map((item) => String(item || "").trim()).filter(Boolean);
  }

  if (msg == null || msg === "") return [];

  return String(msg)
    .split(/\r?\n/)
    .map((line) => line.replace(/^[-•*]\s*/, "").trim())
    .filter((line) => {
      if (!line) return false;
      const normalized = line.toLowerCase();
      return (
        !normalized.includes("seguinte") &&
        !normalized.includes("obrigatório(s) estão vazio")
      );
    });
}

function isRequiredFieldsDialog(dialogTitle) {
  return String(dialogTitle || "")
    .toLowerCase()
    .includes("obrigat");
}

function getSelectPopupContainer(trigger) {
  return trigger?.closest?.(".MuiDialog-root") || document.body;
}

function resolveCreatedVehicle(response, payload) {
  const body = response?.data;
  const created =
    body?.data && typeof body.data === "object" && !Array.isArray(body.data)
      ? body.data
      : body && typeof body === "object" && body.id
        ? body
        : null;

  if (created?.id) {
    return { ...payload, ...created };
  }

  return created;
}

function QuickVehicleModal({ open, onClose, onCreated }) {
  const user = useSelector((state) => state.user);
  const savingRef = useRef(false);
  const openRef = useRef(open);
  openRef.current = open;

  const [form, setForm] = useState(createInitialFormState);
  const [carregando, setCarregando] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertMsg, setAlertMsg] = useState("");
  const [alertTitle, setAlertTitle] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);

  const {
    vehiclePlate,
    renavam,
    ufVeiculo,
    documentCRLV,
    vehicleType,
    rntrc,
    tipoRNTRC,
    bodyworkType,
    documentAnttFrontImg,
    reboques,
  } = form;

  const requiresReboque = isVehicleTypeRequiringReboque(vehicleType);
  const isBusy = carregando || uploading;

  const updateForm = (patch) => {
    setForm((prev) => ({ ...prev, ...patch }));
  };

  useEffect(() => {
    if (open) return;

    setForm(createInitialFormState());
    setFieldErrors({});
    setHasAttemptedSubmit(false);
    setAlertOpen(false);
    setAlertMsg("");
    setAlertTitle("");
    setCarregando(false);
    setUploading(false);
    savingRef.current = false;
  }, [open]);

  useEffect(() => {
    if (!open || !hasAttemptedSubmit) return;

    const result = validateVehicleForm(form);
    setFieldErrors(result.success ? {} : result.fieldErrors);
  }, [open, hasAttemptedSubmit, form]);

  const showDialog = (dialogTitle, dialogMsg) => {
    setAlertTitle(dialogTitle);
    setAlertMsg(dialogMsg);
    setAlertOpen(true);
  };

  const handleAlertClose = () => setAlertOpen(false);

  const handleRequestClose = () => {
    if (carregando) return;
    onClose?.();
  };

  const handleUpload = (file, onReady) => {
    if (!file) return;
    setUploading(true);
    const storageRef = ref(storage, `vehicle/new/${file.name}`);
    const uploadTask = uploadBytesResumable(storageRef, file);

    uploadTask.on(
      "state_changed",
      () => {},
      (error) => {
        console.error(error);
        setUploading(false);
        if (!openRef.current) return;
        showDialog("Erro", "Falha no upload do documento.");
      },
      async () => {
        const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
        setUploading(false);
        if (!openRef.current) return;
        onReady(downloadURL);
      },
    );
  };

  const handleVehicleTypeChange = (value) => {
    const nextType = value || "";
    if (isVehicleTypeRequiringReboque(nextType)) {
      updateForm({
        vehicleType: nextType,
        pesoTara: "",
        capacidadeKG: "",
        capacidadeM3: "",
        bodyworkType: "",
        bodyworkPlate: "",
      });
      return;
    }
    updateForm({ vehicleType: nextType });
  };

  const updateReboqueField = (index, field, value) => {
    setForm((prev) => {
      const nextReboques = [...prev.reboques];
      nextReboques[index] = { ...nextReboques[index], [field]: value };
      return { ...prev, reboques: nextReboques };
    });
  };

  const addReboque = () => {
    setForm((prev) => ({
      ...prev,
      reboques: [...prev.reboques, emptyReboque()],
    }));
  };

  const removeReboque = (index) => {
    setForm((prev) => ({
      ...prev,
      reboques: prev.reboques.filter((_, i) => i !== index),
    }));
  };

  const getFieldError = (path) => fieldErrors[path] || "";

  const buildPayload = () => ({
    uidShipper: user?.uidShipper || "",
    vehiclePlate,
    renavam: renavam || "",
    ufVeiculo: ufVeiculo || "",
    documentCRLV: documentCRLV || "",
    descricaoVeiculo: form.descricaoVeiculo || "",
    vehicleType: vehicleType || "",
    rntrc: rntrc || "",
    ...(tipoRNTRC ? { tipoRNTRC } : {}),
    bodyworkType: bodyworkType || "",
    bodyworkPlate: form.bodyworkPlate || "",
    documentAnttFrontImg: documentAnttFrontImg || "",
    pesoTara: form.pesoTara || "",
    capacidadeKG: form.capacidadeKG || "",
    capacidadeM3: form.capacidadeM3 || "",
    ...(form.tipoProprietario ? { tipoProprietario: form.tipoProprietario } : {}),
    cpfoucnpj: form.cpfoucnpj || "",
    nomeProprietario: form.nomeProprietario || "",
    vehicleImg: form.vehicleImg || "",
    reboques: reboques.filter(isReboqueStarted).map((response) => ({
      ...(response.id ? { id: response.id } : {}),
      placaReboque: response.placaReboque,
      renavamReboque: response.renavamReboque || "",
      ufReboque: response.ufReboque || "",
      descricaoReboque: response.descricaoReboque || "",
      rntrcReboque: response.rntrcReboque || "",
      ...(response.tipoRNTRCReboque ? { tipoRNTRCReboque: response.tipoRNTRCReboque } : {}),
      TipoCarroceriaReboque: response.TipoCarroceriaReboque || "",
      anttReboque: response.anttReboque || "",
      cnhReboque: response.cnhReboque || "",
      capacidadeKGReboque: response.capacidadeKGReboque || "",
      capacidadeM3Reboque: response.capacidadeM3Reboque || "",
      pesoTaraReboque: response.pesoTaraReboque || "",
      ...(response.tipoProprietarioReboque
        ? { tipoProprietarioReboque: response.tipoProprietarioReboque }
        : {}),
      cpfoucnpjReboque: response.cpfoucnpjReboque || "",
      nomeProprietarioReboque: response.nomeProprietarioReboque || "",
    })),
  });

  const save = async () => {
    if (isBusy || savingRef.current) return;

    setHasAttemptedSubmit(true);
    const validation = validateVehicleForm(form);
    if (!validation.success) {
      setFieldErrors(validation.fieldErrors);
      showDialog("Campos obrigatórios faltando", validation.messages);
      window.setTimeout(() => {
        focusFirstInvalidField(validation.firstFieldPath);
      }, 0);
      return;
    }

    savingRef.current = true;
    setFieldErrors({});
    setCarregando(true);
    try {
      const payload = buildPayload();
      const response = await vehicleService.create(payload);
      const created = resolveCreatedVehicle(response, payload);
      const message =
        response?.data?.message || "Veículo cadastrado com sucesso";

      if (!created?.id) {
        showDialog("Erro", "Não foi possível salvar o veículo.");
        return;
      }

      onCreated?.(created, message);
      if (!onCreated) {
        onClose?.();
      }
    } catch (error) {
      const apiErrors = error?.response?.data?.errors;
      const message =
        error?.response?.data?.message ||
        (Array.isArray(apiErrors) && apiErrors[0]?.message) ||
        "Não foi possível salvar o veículo.";
      showDialog("Erro", message);
    } finally {
      savingRef.current = false;
      setCarregando(false);
    }
  };

  const dialogMessageItems = parseDialogMessageItems(alertMsg);
  const requiredFieldsDialog = isRequiredFieldsDialog(alertTitle);
  const showErrorList =
    requiredFieldsDialog ||
    (Array.isArray(alertMsg) && dialogMessageItems.length > 0);
  const dialogDescription = requiredFieldsDialog
    ? "Os seguintes campos obrigatórios estão vazios:"
    : null;
  const dialogBodyText = !showErrorList ? String(alertMsg || "") : "";
  const DialogAlertIcon = requiredFieldsDialog
    ? WarningAmberRoundedIcon
    : ErrorOutlineRoundedIcon;

  return (
    <>
      <Dialog
        open={open}
        onClose={handleRequestClose}
        fullWidth
        maxWidth="md"
        scroll="paper"
        aria-labelledby="quick-vehicle-modal-title"
        PaperProps={{ sx: modalPaperSx }}
      >
        <Box
          className="new-vehicle-page"
          style={newClientThemeVars}
          sx={{ display: "flex", flexDirection: "column", minHeight: 0 }}
        >
          <Box sx={modalHeaderSx}>
            <Typography
              id="quick-vehicle-modal-title"
              component="h2"
              sx={modalTitleSx}
            >
              Cadastro rápido de veículo
            </Typography>
          </Box>

          <DialogContent sx={modalContentSx} dividers>
            <Typography sx={sectionTitleSx}>Dados do veículo</Typography>
            <Box className="new-vehicle-grid">
              <Field
                label="Placa do veículo *"
                span={3}
                fieldPath="vehiclePlate"
                error={getFieldError("vehiclePlate")}
              >
                <InputMask
                  mask="aaa-9*99"
                  maskChar=""
                  className="form-control"
                  value={vehiclePlate}
                  onChange={(e) =>
                    updateForm({ vehiclePlate: e.target.value.toUpperCase() })
                  }
                >
                  {(inputProps) => (
                    <input
                      {...inputProps}
                      aria-invalid={Boolean(getFieldError("vehiclePlate"))}
                      aria-describedby={
                        getFieldError("vehiclePlate")
                          ? "vehicle-field-vehiclePlate-error"
                          : undefined
                      }
                    />
                  )}
                </InputMask>
              </Field>

              <Field
                label="Renavam *"
                span={3}
                fieldPath="renavam"
                error={getFieldError("renavam")}
              >
                <Input
                  maxLength={11}
                  status={getFieldError("renavam") ? "error" : undefined}
                  value={renavam}
                  onChange={(e) =>
                    updateForm({
                      renavam: e.target.value.replace(/\D/g, ""),
                    })
                  }
                  aria-invalid={Boolean(getFieldError("renavam"))}
                  aria-describedby={
                    getFieldError("renavam")
                      ? "vehicle-field-renavam-error"
                      : undefined
                  }
                />
              </Field>

              <Field
                label="UF Veículo *"
                span={2}
                fieldPath="ufVeiculo"
                error={getFieldError("ufVeiculo")}
              >
                <Select
                  style={{ width: "100%" }}
                  status={getFieldError("ufVeiculo") ? "error" : undefined}
                  value={ufVeiculo || undefined}
                  placeholder="Selecione"
                  onChange={(value) => updateForm({ ufVeiculo: value || "" })}
                  options={STATES_UF.map((s) => ({
                    value: s.value,
                    label: `${s.label} (${s.value})`,
                  }))}
                  showSearch
                  optionFilterProp="label"
                  allowClear
                  getPopupContainer={getSelectPopupContainer}
                  dropdownStyle={{ zIndex: 2000 }}
                  aria-invalid={Boolean(getFieldError("ufVeiculo"))}
                  aria-describedby={
                    getFieldError("ufVeiculo")
                      ? "vehicle-field-ufVeiculo-error"
                      : undefined
                  }
                />
              </Field>

              <Field
                label="Tipo de veículo *"
                span={4}
                fieldPath="vehicleType"
                error={getFieldError("vehicleType")}
              >
                <Select
                  style={{ width: "100%" }}
                  status={getFieldError("vehicleType") ? "error" : undefined}
                  value={vehicleType || undefined}
                  placeholder="Selecione"
                  onChange={handleVehicleTypeChange}
                  options={VEHICLE_TYPES.map((t) => ({
                    value: t,
                    label: t,
                  }))}
                  allowClear
                  getPopupContainer={getSelectPopupContainer}
                  dropdownStyle={{ zIndex: 2000 }}
                  aria-invalid={Boolean(getFieldError("vehicleType"))}
                  aria-describedby={
                    getFieldError("vehicleType")
                      ? "vehicle-field-vehicleType-error"
                      : undefined
                  }
                />
              </Field>

              <Field
                label="RNTRC *"
                span={2}
                fieldPath="rntrc"
                error={getFieldError("rntrc")}
              >
                <Input
                  maxLength={8}
                  status={getFieldError("rntrc") ? "error" : undefined}
                  value={rntrc}
                  onChange={(e) =>
                    updateForm({ rntrc: e.target.value.replace(/\D/g, "") })
                  }
                  aria-invalid={Boolean(getFieldError("rntrc"))}
                  aria-describedby={
                    getFieldError("rntrc")
                      ? "vehicle-field-rntrc-error"
                      : undefined
                  }
                />
              </Field>

              <Field
                label="Tipo RNTRC *"
                span={2}
                fieldPath="tipoRNTRC"
                error={getFieldError("tipoRNTRC")}
              >
                <Select
                  style={{ width: "100%" }}
                  status={getFieldError("tipoRNTRC") ? "error" : undefined}
                  value={tipoRNTRC || undefined}
                  placeholder="Selecione"
                  onChange={(value) => updateForm({ tipoRNTRC: value || "" })}
                  options={RNTRC_TYPE_OPTIONS}
                  allowClear
                  getPopupContainer={getSelectPopupContainer}
                  dropdownStyle={{ zIndex: 2000 }}
                  aria-invalid={Boolean(getFieldError("tipoRNTRC"))}
                  aria-describedby={
                    getFieldError("tipoRNTRC")
                      ? "vehicle-field-tipoRNTRC-error"
                      : undefined
                  }
                />
              </Field>

              {!requiresReboque && (
                <Field
                  label="Tipo Carroceria *"
                  span={3}
                  fieldPath="bodyworkType"
                  error={getFieldError("bodyworkType")}
                >
                  <Select
                    style={{ width: "100%" }}
                    status={getFieldError("bodyworkType") ? "error" : undefined}
                    value={bodyworkType || undefined}
                    placeholder="Selecione"
                    onChange={(value) =>
                      updateForm({ bodyworkType: value || "" })
                    }
                    options={BODYWORK_TYPES.map((t) => ({
                      value: t,
                      label: t,
                    }))}
                    allowClear
                    getPopupContainer={getSelectPopupContainer}
                    dropdownStyle={{ zIndex: 2000 }}
                    aria-invalid={Boolean(getFieldError("bodyworkType"))}
                    aria-describedby={
                      getFieldError("bodyworkType")
                        ? "vehicle-field-bodyworkType-error"
                        : undefined
                    }
                  />
                </Field>
              )}
            </Box>

            <Typography sx={{ ...sectionTitleSx, mt: 2.5 }}>
              Documentos
            </Typography>
            <Box className="new-vehicle-grid">
              <Field
                label="CRLV *"
                span={6}
                fieldPath="documentCRLV"
                error={getFieldError("documentCRLV")}
              >
                <DocumentField
                  url={documentCRLV}
                  disabled={uploading}
                  error={Boolean(getFieldError("documentCRLV"))}
                  errorId="vehicle-field-documentCRLV-error"
                  onUpload={(file) =>
                    handleUpload(file, (url) =>
                      updateForm({ documentCRLV: url }),
                    )
                  }
                  onClear={() => updateForm({ documentCRLV: "" })}
                />
              </Field>
              <Field
                label="Doc ANTT *"
                span={6}
                fieldPath="documentAnttFrontImg"
                error={getFieldError("documentAnttFrontImg")}
              >
                <DocumentField
                  url={documentAnttFrontImg}
                  disabled={uploading}
                  error={Boolean(getFieldError("documentAnttFrontImg"))}
                  errorId="vehicle-field-documentAnttFrontImg-error"
                  onUpload={(file) =>
                    handleUpload(file, (url) =>
                      updateForm({ documentAnttFrontImg: url }),
                    )
                  }
                  onClear={() => updateForm({ documentAnttFrontImg: "" })}
                />
              </Field>
            </Box>

            {requiresReboque && (
              <Box sx={{ mt: 2.5 }}>
                <Typography sx={sectionTitleSx}>Reboques</Typography>
                <Typography sx={sectionSubtitleSx}>
                  Este tipo de veículo exige o cadastro de pelo menos 1 reboque
                  completo.
                </Typography>

                {getFieldError("reboques") ? (
                  <Typography
                    id="vehicle-field-reboques-error"
                    role="alert"
                    className="new-vehicle-field-error"
                    sx={{ mb: 1.5, mt: 1 }}
                    data-field-path="reboques"
                  >
                    {getFieldError("reboques")}
                  </Typography>
                ) : null}

                <Box sx={{ mb: 2, mt: 1.5 }}>
                  <Button
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={addReboque}
                    sx={secondaryActionSx}
                  >
                    Adicionar reboque
                  </Button>
                </Box>

                {reboques.length === 0 ? (
                  <Typography sx={{ color: "rgba(1,36,67,0.62)", fontSize: 14 }}>
                    Nenhum reboque vinculado.
                  </Typography>
                ) : (
                  reboques.map((reboque, index) => {
                    const placaPath = `reboques.${index}.placaReboque`;
                    const renavamPath = `reboques.${index}.renavamReboque`;
                    const ufPath = `reboques.${index}.ufReboque`;
                    const rntrcPath = `reboques.${index}.rntrcReboque`;
                    const tipoRntrcPath = `reboques.${index}.tipoRNTRCReboque`;
                    const carroceriaPath = `reboques.${index}.TipoCarroceriaReboque`;
                    const anttPath = `reboques.${index}.anttReboque`;

                    return (
                      <Box
                        key={reboque.id || index}
                        className="reboque-block"
                      >
                        <Box className="reboque-block-header">
                          <Typography sx={sectionTitleSx}>
                            Reboque #{index + 1}
                          </Typography>
                          <Button
                            color="error"
                            startIcon={<DeleteIcon />}
                            onClick={() => removeReboque(index)}
                          >
                            Remover
                          </Button>
                        </Box>

                        <Box className="new-vehicle-grid">
                          <Field
                            label="Placa Reboque *"
                            span={3}
                            fieldPath={placaPath}
                            error={getFieldError(placaPath)}
                          >
                            <InputMask
                              mask="aaa-9*99"
                              maskChar=""
                              className="form-control"
                              value={reboque.placaReboque}
                              onChange={(e) =>
                                updateReboqueField(
                                  index,
                                  "placaReboque",
                                  e.target.value.toUpperCase(),
                                )
                              }
                            >
                              {(inputProps) => (
                                <input
                                  {...inputProps}
                                  aria-invalid={Boolean(
                                    getFieldError(placaPath),
                                  )}
                                  aria-describedby={
                                    getFieldError(placaPath)
                                      ? `vehicle-field-${placaPath.replace(/\./g, "-")}-error`
                                      : undefined
                                  }
                                />
                              )}
                            </InputMask>
                          </Field>

                          <Field
                            label="Renavam Reboque *"
                            span={3}
                            fieldPath={renavamPath}
                            error={getFieldError(renavamPath)}
                          >
                            <Input
                              maxLength={11}
                              status={
                                getFieldError(renavamPath) ? "error" : undefined
                              }
                              value={reboque.renavamReboque}
                              onChange={(e) =>
                                updateReboqueField(
                                  index,
                                  "renavamReboque",
                                  e.target.value.replace(/\D/g, ""),
                                )
                              }
                              aria-invalid={Boolean(getFieldError(renavamPath))}
                              aria-describedby={
                                getFieldError(renavamPath)
                                  ? `vehicle-field-${renavamPath.replace(/\./g, "-")}-error`
                                  : undefined
                              }
                            />
                          </Field>

                          <Field
                            label="UF Reboque *"
                            span={2}
                            fieldPath={ufPath}
                            error={getFieldError(ufPath)}
                          >
                            <Select
                              style={{ width: "100%" }}
                              status={
                                getFieldError(ufPath) ? "error" : undefined
                              }
                              value={reboque.ufReboque || undefined}
                              placeholder="Selecione"
                              onChange={(v) =>
                                updateReboqueField(index, "ufReboque", v || "")
                              }
                              options={STATES_UF.map((s) => ({
                                value: s.value,
                                label: `${s.label} (${s.value})`,
                              }))}
                              showSearch
                              optionFilterProp="label"
                              allowClear
                              getPopupContainer={getSelectPopupContainer}
                              dropdownStyle={{ zIndex: 2000 }}
                              aria-invalid={Boolean(getFieldError(ufPath))}
                              aria-describedby={
                                getFieldError(ufPath)
                                  ? `vehicle-field-${ufPath.replace(/\./g, "-")}-error`
                                  : undefined
                              }
                            />
                          </Field>

                          <Field
                            label="RNTRC Reboque *"
                            span={2}
                            fieldPath={rntrcPath}
                            error={getFieldError(rntrcPath)}
                          >
                            <Input
                              maxLength={8}
                              status={
                                getFieldError(rntrcPath) ? "error" : undefined
                              }
                              value={reboque.rntrcReboque}
                              onChange={(e) =>
                                updateReboqueField(
                                  index,
                                  "rntrcReboque",
                                  e.target.value.replace(/\D/g, ""),
                                )
                              }
                              aria-invalid={Boolean(getFieldError(rntrcPath))}
                              aria-describedby={
                                getFieldError(rntrcPath)
                                  ? `vehicle-field-${rntrcPath.replace(/\./g, "-")}-error`
                                  : undefined
                              }
                            />
                          </Field>

                          <Field
                            label="Tipo RNTRC Reboque *"
                            span={2}
                            fieldPath={tipoRntrcPath}
                            error={getFieldError(tipoRntrcPath)}
                          >
                            <Select
                              style={{ width: "100%" }}
                              status={
                                getFieldError(tipoRntrcPath)
                                  ? "error"
                                  : undefined
                              }
                              value={reboque.tipoRNTRCReboque || undefined}
                              placeholder="Selecione"
                              onChange={(v) =>
                                updateReboqueField(
                                  index,
                                  "tipoRNTRCReboque",
                                  v || "",
                                )
                              }
                              options={RNTRC_TYPE_OPTIONS}
                              allowClear
                              getPopupContainer={getSelectPopupContainer}
                              dropdownStyle={{ zIndex: 2000 }}
                              aria-invalid={Boolean(
                                getFieldError(tipoRntrcPath),
                              )}
                              aria-describedby={
                                getFieldError(tipoRntrcPath)
                                  ? `vehicle-field-${tipoRntrcPath.replace(/\./g, "-")}-error`
                                  : undefined
                              }
                            />
                          </Field>

                          <Field
                            label="Tipo Carroceria Reboque *"
                            span={3}
                            fieldPath={carroceriaPath}
                            error={getFieldError(carroceriaPath)}
                          >
                            <Select
                              style={{ width: "100%" }}
                              status={
                                getFieldError(carroceriaPath)
                                  ? "error"
                                  : undefined
                              }
                              value={reboque.TipoCarroceriaReboque || undefined}
                              placeholder="Selecione"
                              onChange={(v) =>
                                updateReboqueField(
                                  index,
                                  "TipoCarroceriaReboque",
                                  v || "",
                                )
                              }
                              options={BODYWORK_TYPES.map((t) => ({
                                value: t,
                                label: t,
                              }))}
                              allowClear
                              getPopupContainer={getSelectPopupContainer}
                              dropdownStyle={{ zIndex: 2000 }}
                              aria-invalid={Boolean(
                                getFieldError(carroceriaPath),
                              )}
                              aria-describedby={
                                getFieldError(carroceriaPath)
                                  ? `vehicle-field-${carroceriaPath.replace(/\./g, "-")}-error`
                                  : undefined
                              }
                            />
                          </Field>

                          <Field
                            label="ANTT Reboque *"
                            span={6}
                            fieldPath={anttPath}
                            error={getFieldError(anttPath)}
                          >
                            <DocumentField
                              url={reboque.anttReboque}
                              disabled={uploading}
                              error={Boolean(getFieldError(anttPath))}
                              errorId={`vehicle-field-${anttPath.replace(/\./g, "-")}-error`}
                              onUpload={(file) =>
                                handleUpload(file, (url) =>
                                  updateReboqueField(index, "anttReboque", url),
                                )
                              }
                              onClear={() =>
                                updateReboqueField(index, "anttReboque", "")
                              }
                            />
                          </Field>
                        </Box>
                      </Box>
                    );
                  })
                )}
              </Box>
            )}
          </DialogContent>

          <DialogActions sx={modalActionsSx}>
            <Button
              onClick={handleRequestClose}
              variant="outlined"
              disabled={carregando}
              sx={secondaryActionSx}
            >
              Cancelar
            </Button>
            <Button
              disabled={isBusy}
              variant="contained"
              startIcon={
                carregando ? (
                  <CircularProgress color="inherit" size={16} />
                ) : null
              }
              onClick={save}
              sx={primaryActionSx}
            >
              {carregando ? "Cadastrando..." : "Cadastrar"}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>

      <Dialog
        open={alertOpen}
        onClose={handleAlertClose}
        role="dialog"
        aria-modal="true"
        aria-labelledby={ALERT_TITLE_ID}
        aria-describedby={ALERT_DESCRIPTION_ID}
        fullWidth
        maxWidth="sm"
        scroll="paper"
        BackdropProps={{
          sx: dialogBackdropSx,
        }}
        PaperProps={{
          sx: alertPaperSx,
        }}
      >
        <Box sx={dialogHeaderSx}>
          <Box sx={dialogIconWrapSx}>
            <DialogAlertIcon
              sx={{
                fontSize: 26,
                color: theme.colors.highlight,
              }}
            />
          </Box>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography id={ALERT_TITLE_ID} component="h2" sx={dialogTitleSx}>
              {alertTitle || "Atenção"}
            </Typography>
            {dialogDescription ? (
              <Typography id={ALERT_DESCRIPTION_ID} sx={dialogSubtitleSx}>
                {dialogDescription}
              </Typography>
            ) : null}
          </Box>
        </Box>

        <DialogContent
          id={dialogDescription ? undefined : ALERT_DESCRIPTION_ID}
          sx={dialogContentSx}
        >
          {showErrorList ? (
            <Box
              component="ul"
              sx={dialogErrorListSx}
              aria-label="Lista de campos obrigatórios"
            >
              {dialogMessageItems.map((item, index) => (
                <Box
                  component="li"
                  key={`${item}-${index}`}
                  sx={dialogErrorItemSx}
                >
                  <Box component="span" sx={dialogErrorBulletSx} />
                  <Typography component="span" sx={dialogErrorTextSx}>
                    {item}
                  </Typography>
                </Box>
              ))}
            </Box>
          ) : (
            <Typography sx={dialogBodyTextSx}>{dialogBodyText}</Typography>
          )}
        </DialogContent>

        <Divider sx={{ borderColor: theme.colors.primaryBorder }} />

        <DialogActions sx={dialogActionsSx}>
          <Button
            onClick={handleAlertClose}
            variant="contained"
            autoFocus
            disableElevation
            sx={dialogPrimaryButtonSx}
          >
            Ok
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

function Field({ label, span = 3, error, fieldPath, children }) {
  const errorId = fieldPath
    ? `vehicle-field-${String(fieldPath).replace(/\./g, "-")}-error`
    : undefined;

  return (
    <div
      className={`new-vehicle-field new-vehicle-span-${span}${error ? " has-error" : ""}`}
      data-field-path={fieldPath || undefined}
    >
      <label>{label}</label>
      {children}
      {error ? (
        <span id={errorId} className="new-vehicle-field-error" role="alert">
          {error}
        </span>
      ) : null}
    </div>
  );
}

const modalPaperSx = {
  borderRadius: `${theme.radius.large}px`,
  overflow: "hidden",
};

const modalHeaderSx = {
  px: { xs: 2, sm: 2.5 },
  pt: { xs: 2, sm: 2.5 },
  pb: 1.5,
  borderBottom: `1px solid ${theme.colors.primaryBorder}`,
};

const modalTitleSx = {
  color: theme.colors.primary,
  fontFamily: theme.typography.fontFamily,
  fontWeight: theme.typography.weightBold,
  fontSize: { xs: "1.05rem", sm: "1.2rem" },
  lineHeight: 1.3,
};

const modalContentSx = {
  px: { xs: 2, sm: 2.5 },
  py: { xs: 2, sm: 2.25 },
};

const modalActionsSx = {
  px: { xs: 2, sm: 2.5 },
  py: { xs: 1.5, sm: 2 },
  justifyContent: "flex-end",
  gap: 1,
  borderTop: `1px solid ${theme.colors.primaryBorder}`,
};

const sectionTitleSx = {
  color: "var(--new-client-primary)",
  fontWeight: 700,
  fontSize: "1.05rem",
  mb: 1.5,
};

const sectionSubtitleSx = {
  color: "rgba(1, 36, 67, 0.62)",
  fontSize: "0.79rem",
  lineHeight: 1.45,
};

const primaryActionSx = {
  textTransform: "none",
  fontWeight: theme.typography.weightSemibold,
  borderRadius: `${theme.radius.medium - 2}px`,
  backgroundColor: "var(--new-client-primary)",
  minHeight: 40,
  px: 2,
  "&:hover": {
    backgroundColor: "var(--new-client-primary-hover)",
  },
};

const secondaryActionSx = {
  textTransform: "none",
  fontWeight: theme.typography.weightSemibold,
  borderRadius: `${theme.radius.medium - 2}px`,
  minHeight: 40,
  color: "var(--new-client-primary)",
  borderColor: "var(--new-client-primary-border)",
};

const dialogBackdropSx = {
  backgroundColor: "rgba(1, 36, 66, 0.48)",
  backdropFilter: "blur(4px)",
  WebkitBackdropFilter: "blur(4px)",
};

const alertPaperSx = {
  borderRadius: `${theme.radius.large}px`,
  backgroundColor: theme.colors.surface,
  boxShadow: "0 18px 40px rgba(1, 36, 66, 0.18)",
  overflow: "hidden",
  width: "100%",
  maxWidth: 520,
  mx: { xs: 2, sm: 3 },
  my: { xs: 2, sm: 3 },
  maxHeight: "min(88vh, 640px)",
  display: "flex",
  flexDirection: "column",
  transition: theme.motion.transition,
};

const dialogHeaderSx = {
  display: "flex",
  alignItems: "flex-start",
  gap: `${theme.spacing.sm}px`,
  px: { xs: 2, sm: 2.5 },
  pt: { xs: 2, sm: 2.5 },
  pb: { xs: 1.5, sm: 2 },
  backgroundColor: theme.colors.highlightSoft,
  borderBottom: `1px solid ${theme.colors.primaryBorder}`,
};

const dialogIconWrapSx = {
  width: 44,
  height: 44,
  borderRadius: "50%",
  backgroundColor: theme.colors.surface,
  border: `1px solid ${theme.colors.primaryBorder}`,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
  boxShadow: "0 4px 12px rgba(1, 36, 66, 0.08)",
};

const dialogTitleSx = {
  color: theme.colors.primary,
  fontFamily: theme.typography.fontFamily,
  fontWeight: theme.typography.weightBold,
  fontSize: { xs: "1.05rem", sm: "1.2rem" },
  lineHeight: 1.3,
  letterSpacing: theme.typography.letterSpacing,
};

const dialogSubtitleSx = {
  mt: 0.5,
  color: theme.colors.textSecondary,
  fontFamily: theme.typography.fontFamily,
  fontWeight: theme.typography.weightRegular,
  fontSize: theme.typography.bodySize,
  lineHeight: theme.typography.bodyLineHeight,
};

const dialogContentSx = {
  px: { xs: 2, sm: 2.5 },
  py: { xs: 1.75, sm: 2 },
  overflowY: "auto",
  maxHeight: { xs: "42vh", sm: "46vh" },
};

const dialogErrorListSx = {
  listStyle: "none",
  m: 0,
  p: 0,
  display: "flex",
  flexDirection: "column",
  gap: `${theme.spacing.xs}px`,
};

const dialogErrorItemSx = {
  display: "flex",
  alignItems: "flex-start",
  gap: `${theme.spacing.xs}px`,
  px: 1.25,
  py: 1,
  borderRadius: `${theme.radius.small}px`,
  backgroundColor: theme.colors.highlightSoft,
  border: `1px solid ${theme.colors.primaryBorder}`,
};

const dialogErrorBulletSx = {
  width: 7,
  height: 7,
  borderRadius: "50%",
  backgroundColor: theme.colors.highlight,
  flexShrink: 0,
  mt: "0.45rem",
};

const dialogErrorTextSx = {
  color: theme.colors.textPrimary,
  fontFamily: theme.typography.fontFamily,
  fontSize: theme.typography.bodySize,
  lineHeight: 1.45,
  fontWeight: theme.typography.weightSemibold,
  wordBreak: "break-word",
};

const dialogBodyTextSx = {
  color: theme.colors.textSecondary,
  fontFamily: theme.typography.fontFamily,
  fontSize: theme.typography.bodySize,
  lineHeight: 1.5,
  whiteSpace: "pre-line",
  wordBreak: "break-word",
};

const dialogActionsSx = {
  px: { xs: 2, sm: 2.5 },
  py: { xs: 1.5, sm: 2 },
  justifyContent: { xs: "stretch", sm: "flex-end" },
  gap: 1,
};

const dialogPrimaryButtonSx = {
  textTransform: "none",
  fontFamily: theme.typography.fontFamily,
  fontWeight: theme.typography.weightSemibold,
  borderRadius: `${theme.radius.medium - 2}px`,
  backgroundColor: theme.colors.primary,
  color: theme.colors.white,
  border: `1px solid ${theme.colors.primary}`,
  minHeight: 42,
  minWidth: { xs: "100%", sm: 112 },
  px: 2.5,
  transition: theme.motion.transition,
  "&:hover": {
    backgroundColor: theme.colors.white,
    color: theme.colors.primary,
    borderColor: theme.colors.primary,
  },
};

export default QuickVehicleModal;
